import { useMemo, useState } from 'react';
import { FiDelete, FiMinus, FiPlus } from 'react-icons/fi';
import { LuPlusSquare } from 'react-icons/lu';
import DraggableBottomSheet from './DraggableBottomSheet';
import CurrencyDisplay from './AmountDisplay';
import {
  Account,
  PortfolioHolding,
  PortfolioTradeRequest,
  PortfolioTradeType,
} from '../types';
import { createPortfolioTrade } from '../services/apiService';
import { getTodayKolkataDateInputValue } from '../utils/dateTimeUtils';
import { formatCurrency } from '../utils/formatters';
import { useDisplayPrivacy } from '../contexts/DisplayPrivacyContext';
import { getDefaultPortfolioAssetName, getPortfolioAssetTypeForAccountType } from '../utils/portfolioUtils';

interface AddPortfolioTradeFlowProps {
  account: Account;
  holdings: PortfolioHolding[];
  onTradeCreated: () => Promise<void> | void;
}

const numpadLayout = [
  ['1', '2', '3'],
  ['4', '5', '6'],
  ['7', '8', '9'],
  ['.', '0', 'backspace'],
];

function AddPortfolioTradeFlow({ account, holdings, onTradeCreated }: AddPortfolioTradeFlowProps) {
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);
  const [unitPrice, setUnitPrice] = useState<number | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const openFlow = () => {
    setUnitPrice(null);
    setIsDetailsOpen(false);
    setIsPriceModalOpen(true);
  };

  const handlePriceSubmit = (price: number) => {
    setUnitPrice(price);
    setIsPriceModalOpen(false);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setUnitPrice(null);
  };

  return (
    <>
      <button
        type="button"
        className="text-foreground hover:text-foreground"
        onClick={openFlow}
        aria-label="Add portfolio transaction"
      >
        <LuPlusSquare className="h-6 w-6" />
      </button>

      <PortfolioUnitPriceModal
        isOpen={isPriceModalOpen}
        onClose={() => setIsPriceModalOpen(false)}
        onSubmit={handlePriceSubmit}
      />

      {unitPrice != null && (
        <PortfolioTradeDetailsSheet
          isOpen={isDetailsOpen}
          account={account}
          unitPrice={unitPrice}
          holdings={holdings}
          onClose={handleCloseDetails}
          onTradeCreated={async () => {
            await onTradeCreated();
            handleCloseDetails();
          }}
        />
      )}
    </>
  );
}

function PortfolioUnitPriceModal({
  isOpen,
  onClose,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (price: number) => void;
}) {
  const [amountString, setAmountString] = useState('0');
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setAmountString('0');
    setError(null);
    onClose();
  };

  const handleNumpadClick = (value: string) => {
    setError(null);
    if (amountString.includes('.') && value === '.') {
      return;
    }
    const nextAmountString = amountString === '0' && value !== '.' ? value : amountString + value;
    const parts = nextAmountString.split('.');
    if (parts.length > 1 && parts[1].length > 2) {
      return;
    }
    const potentialAmount = parseFloat(nextAmountString);
    if (!Number.isNaN(potentialAmount) && potentialAmount > 999999999) {
      return;
    }
    setAmountString(nextAmountString);
  };

  const handleBackspace = () => {
    setError(null);
    setAmountString((previous) => (previous.length > 1 ? previous.slice(0, -1) : '0'));
  };

  const handleSubmit = () => {
    const price = parseFloat(amountString);
    if (!Number.isFinite(price) || price <= 0) {
      setError('Unit price must be greater than zero.');
      return;
    }
    setAmountString('0');
    setError(null);
    onSubmit(price);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4 text-foreground backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-sm origin-bottom flex-col overflow-hidden rounded-3xl border border-border/80 bg-card shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex-shrink-0 p-4 pt-6">
          {error && <p className="mb-2 text-center text-sm text-red-500">{error}</p>}
          <div className="mb-2 text-sm font-medium text-muted-foreground">Unit price</div>
          <CurrencyDisplay
            amount={parseFloat(amountString) || 0}
            smallRupeeSymbol
            className="text-3xl font-semibold"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto border-t border-border p-3">
          <div className="grid grid-cols-3 gap-3">
            {numpadLayout.flat().map((key) => (
              <button
                key={key}
                type="button"
                onClick={() => (key === 'backspace' ? handleBackspace() : handleNumpadClick(key))}
                className="flex items-center justify-center rounded-xl border border-border/70 bg-input/70 py-3 text-xl font-semibold text-foreground transition duration-100 ease-in-out hover:bg-input active:scale-95"
              >
                {key === 'backspace' ? <FiDelete /> : key}
              </button>
            ))}
          </div>

          <div className="mt-3 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleClose}
              className="rounded-lg border border-border/80 bg-input/70 py-2.5 font-semibold text-foreground hover:bg-input"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="rounded-lg bg-primary py-2.5 font-semibold text-primary-foreground hover:bg-primary/90 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-100"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PortfolioTradeDetailsSheet({
  isOpen,
  account,
  unitPrice,
  holdings,
  onClose,
  onTradeCreated,
}: {
  isOpen: boolean;
  account: Account;
  unitPrice: number;
  holdings: PortfolioHolding[];
  onClose: () => void;
  onTradeCreated: () => Promise<void> | void;
}) {
  const { randomizeAmounts } = useDisplayPrivacy();
  const openHoldings = useMemo(
    () => holdings.filter((holding) => holding.quantityHeld > 0 && holding.status !== 'CLOSED'),
    [holdings]
  );
  const defaultAssetType = getPortfolioAssetTypeForAccountType(account.type);
  const [tradeType, setTradeType] = useState<PortfolioTradeType>('BUY');
  const [assetName, setAssetName] = useState(getDefaultPortfolioAssetName(account));
  const [symbol, setSymbol] = useState('');
  const [selectedHoldingKey, setSelectedHoldingKey] = useState('');
  const [quantity, setQuantity] = useState('');
  const [fees, setFees] = useState('0');
  const [tradeDate, setTradeDate] = useState(getTodayKolkataDateInputValue());
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const selectedHolding = useMemo(
    () => openHoldings.find((holding) => holdingKey(holding) === selectedHoldingKey) ?? null,
    [openHoldings, selectedHoldingKey]
  );
  const effectiveAssetType = selectedHolding?.assetType ?? defaultAssetType;
  const effectiveAssetName = selectedHolding?.assetName ?? assetName.trim();
  const effectiveSymbol = selectedHolding ? selectedHolding.symbol ?? '' : symbol.trim();
  const sellDisabled = openHoldings.length === 0;

  const handleTradeTypeChange = (nextTradeType: PortfolioTradeType) => {
    setTradeType(nextTradeType);
    setError(null);
    if (nextTradeType === 'SELL' && openHoldings.length > 0) {
      const firstHolding = openHoldings[0];
      setSelectedHoldingKey(holdingKey(firstHolding));
      setAssetName(firstHolding.assetName);
      setSymbol(firstHolding.symbol ?? '');
    }
  };

  const handleHoldingChange = (key: string) => {
    const nextHolding = openHoldings.find((holding) => holdingKey(holding) === key);
    setSelectedHoldingKey(key);
    setError(null);
    if (nextHolding) {
      setAssetName(nextHolding.assetName);
      setSymbol(nextHolding.symbol ?? '');
    }
  };

  const handleSubmit = async () => {
    setError(null);
    const parsedQuantity = Number(quantity);
    const parsedFees = fees.trim() === '' ? 0 : Number(fees);

    if (tradeType === 'SELL' && !selectedHolding) {
      setError('Choose a holding to sell.');
      return;
    }
    if (!effectiveAssetName) {
      setError('Asset name is required.');
      return;
    }
    if (!Number.isFinite(parsedQuantity) || parsedQuantity <= 0) {
      setError('Quantity must be greater than zero.');
      return;
    }
    if (tradeType === 'SELL' && selectedHolding && parsedQuantity > selectedHolding.quantityHeld) {
      setError(`Quantity cannot exceed ${selectedHolding.quantityHeld.toLocaleString('en-IN')}.`);
      return;
    }
    if (!Number.isFinite(parsedFees) || parsedFees < 0) {
      setError('Fees cannot be negative.');
      return;
    }

    const payload: PortfolioTradeRequest = {
      assetType: effectiveAssetType,
      assetName: effectiveAssetName,
      symbol: effectiveSymbol || null,
      tradeType,
      tradeDate,
      quantity: parsedQuantity,
      price: unitPrice,
      fees: parsedFees,
      currency: account.currency || 'INR',
      notes: notes.trim() || null,
    };

    setIsSubmitting(true);
    try {
      await createPortfolioTrade(account.id, payload);
      await onTradeCreated();
    } catch (caughtError) {
      setError(caughtError instanceof Error ? caughtError.message : 'Failed to create portfolio transaction.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <DraggableBottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title="Portfolio Transaction"
      closeDisabled={isSubmitting}
      closeLoading={isSubmitting}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 pb-6">
        <div className="mb-4 rounded-2xl border border-border bg-card p-4">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Unit price</div>
          <div className="mt-1 text-2xl font-semibold">
            {formatCurrency(unitPrice, { randomize: randomizeAmounts })}
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-xl border border-error/30 bg-error/10 px-4 py-3 text-sm text-error">
            {error}
          </div>
        )}

        <div className="mb-4 grid grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => handleTradeTypeChange('BUY')}
            disabled={isSubmitting}
            className={`flex items-center justify-center gap-2 rounded-xl border py-3 font-semibold ${
              tradeType === 'BUY'
                ? 'border-success/40 bg-success/20 text-success'
                : 'border-border bg-input/70 text-foreground'
            }`}
          >
            <FiPlus />
            Buy
          </button>
          <button
            type="button"
            onClick={() => handleTradeTypeChange('SELL')}
            disabled={isSubmitting || sellDisabled}
            className={`flex items-center justify-center gap-2 rounded-xl border py-3 font-semibold disabled:cursor-not-allowed disabled:opacity-50 ${
              tradeType === 'SELL'
                ? 'border-error/40 bg-error/20 text-error'
                : 'border-border bg-input/70 text-foreground'
            }`}
          >
            <FiMinus />
            Sell
          </button>
        </div>

        {tradeType === 'SELL' ? (
          <label className="mb-4 block">
            <span className="mb-1 block text-sm font-medium text-muted-foreground">Holding</span>
            <select
              value={selectedHoldingKey}
              onChange={(event) => handleHoldingChange(event.target.value)}
              disabled={isSubmitting}
              className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {openHoldings.map((holding) => (
                <option key={holdingKey(holding)} value={holdingKey(holding)}>
                  {holding.assetName} · {holding.quantityHeld.toLocaleString('en-IN')} held
                </option>
              ))}
            </select>
          </label>
        ) : (
          <>
            <label className="mb-4 block">
              <span className="mb-1 block text-sm font-medium text-muted-foreground">Asset name</span>
              <input
                value={assetName}
                onChange={(event) => setAssetName(event.target.value)}
                disabled={isSubmitting}
                placeholder="Asset name"
                className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>

            <label className="mb-4 block">
              <span className="mb-1 block text-sm font-medium text-muted-foreground">Symbol</span>
              <input
                value={symbol}
                onChange={(event) => setSymbol(event.target.value)}
                disabled={isSubmitting}
                placeholder="Optional"
                className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>
          </>
        )}

        <div className="mb-4 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-muted-foreground">Quantity</span>
            <input
              type="number"
              min="0"
              step="any"
              value={quantity}
              onChange={(event) => setQuantity(event.target.value)}
              disabled={isSubmitting}
              className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-muted-foreground">Fees</span>
            <input
              type="number"
              min="0"
              step="0.01"
              value={fees}
              onChange={(event) => setFees(event.target.value)}
              disabled={isSubmitting}
              className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </label>
        </div>

        <label className="mb-4 block">
          <span className="mb-1 block text-sm font-medium text-muted-foreground">Date</span>
          <input
            type="date"
            value={tradeDate}
            max={getTodayKolkataDateInputValue()}
            onChange={(event) => setTradeDate(event.target.value)}
            disabled={isSubmitting}
            className="h-12 w-full rounded-xl border border-border bg-background px-3 text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <label className="mb-5 block">
          <span className="mb-1 block text-sm font-medium text-muted-foreground">Notes</span>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            disabled={isSubmitting}
            rows={3}
            placeholder="Optional"
            className="w-full resize-none rounded-xl border border-border bg-background px-3 py-3 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </label>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="mt-auto rounded-xl bg-primary py-3 font-semibold text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-zinc-200 dark:text-zinc-900 dark:hover:bg-zinc-100"
        >
          {isSubmitting ? 'Creating...' : 'Create transaction'}
        </button>
      </div>
    </DraggableBottomSheet>
  );
}

function holdingKey(holding: PortfolioHolding) {
  return `${holding.assetType}-${holding.symbol ?? ''}-${holding.assetName}`;
}

export default AddPortfolioTradeFlow;
